import Button from '@v2/shared/components/Button';
import Currency from '@v2/shared/components/Currency';
import Container from '@v2/shared/components/Container';
import { faLongArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { useSite } from '@v2/shared/lib/site';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { FeaturedTitle } from './types';

type Props = {
  featuredTitles: FeaturedTitle[];
  offerCode: string | null;
};

/**
 * The shop window: a wall of covers, each with what that title costs on its own.
 *
 * "View Available Magazines" goes to /categories/magzter-gold, which is still
 * headless_jshop's page — only the landing page has moved.
 */
export default function TitlesPanel({ featuredTitles, offerCode }: Props) {
  const { currency } = useSite();
  const { buildUrl } = useUrlGenerator();

  // headless forwarded the whole query string here so a campaign's parameters survived
  // the hop. Only the offer code is forwarded now: it is the one parameter the next
  // page acts on, and it is already a prop, so the link is the same on the server and
  // in the browser. Reading window.location instead would differ between the two and
  // hydration would throw the server's markup away.
  const browseUrl = buildUrl('/categories/magzter-gold') + (offerCode ? `?offer=${encodeURIComponent(offerCode)}` : '');

  return (
    <div className="flex items-center justify-center py-10 md:py-14 bg-white lg:py-20">
      <Container className="flex flex-col items-center">
        <h2 className="text-3xl sm:text-4xl md:text-5xl mb-10 font-bold max-w-3xl text-center">
          1000s of popular titles
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-6 lg:gap-6 w-full mb-6">
          {featuredTitles.map((title) => (
            <div key={title.id} className="rounded-xl">
              <img src={title.image} alt={title.name} className="rounded-md border-2 border-slate-200 mb-1" />
              <div className="font-semibold text-md lg:text-md xl:text-lg">{title.name}</div>
              {title.list_price > 0 && (
                <div className="line-through font-medium tracking-tight text-slate-400">
                  <Currency value={title.list_price} currencyCode={currency} />
                  /year
                </div>
              )}
            </div>
          ))}
        </div>
        <Button size="md" color="secondary" iconAfter={faLongArrowRight} onClick={() => (window.location.href = browseUrl)}>
          View Available Magazines
        </Button>
      </Container>
    </div>
  );
}
